/**
 * Figma Theme
 * Modern design system theme inspired by Figma's brand identity
 * Primary: Purple (#A259FF), Secondary: Light Blue (#4DC7F7), Accent: Teal (#1ABCFE)
 */

import { figmaColors } from '../tokens/colors.js';
import { spacing, zIndex } from '../tokens/spacing.js';
import { typography } from '../tokens/typography.js';
import { borderRadius, shadows, transitions } from '../tokens/effects.js';

export const figmaTheme = {
  name: 'figma',
  label: 'Figma',
  description: 'Modern theme inspired by Figma\'s design system with purple and cyan accents',
  isDark: false,

  // Color palette
  colors: {
    primary: figmaColors.primary,
    primaryLight: figmaColors.primaryLight,
    primaryDark: figmaColors.primaryDark,
    primaryHover: figmaColors.primaryDark,
    primaryOpacity: figmaColors.primaryOpacity20,

    secondary: figmaColors.secondary,
    secondaryLight: figmaColors.secondaryLight,
    secondaryDark: figmaColors.secondaryDark,
    secondaryOpacity: figmaColors.secondaryOpacity20,

    accent: figmaColors.accent,
    accentLight: figmaColors.accentLight,
    accentDark: figmaColors.accentDark,
    accentOpacity: figmaColors.accentOpacity20,

    // Semantic colors
    error: figmaColors.error,
    errorLight: figmaColors.errorLight,
    success: figmaColors.success,
    successLight: figmaColors.successLight,
    warning: figmaColors.warning,
    warningLight: figmaColors.warningLight,
    info: figmaColors.info,
    infoLight: figmaColors.infoLight,

    // Neutrals
    background: figmaColors.backgroundLight,
    backgroundAlt: figmaColors.backgroundDark,
    backgroundDark: figmaColors.backgroundDark,
    backgroundNeutral: '#F0F0F0',
    surface: figmaColors.surfaceLight,
    surfaceElevated: figmaColors.surfaceElevated,

    // Text
    textPrimary: figmaColors.darkGray900,
    textSecondary: figmaColors.mediumGray600,
    textTertiary: figmaColors.mediumGray500,
    textInverse: figmaColors.white,

    // Borders
    borderDefault: figmaColors.lightGray300,
    borderSubtle: figmaColors.lightGray200,
    borderInput: figmaColors.borderInput,
  },

  // Spacing
  spacing,

  // Z-index
  zIndex,

  // Typography
  typography,

  // Border radius
  borderRadius,

  // Shadows
  shadows,

  // Transitions
  transitions,

  // Component-specific tokens
  components: {
    button: {
      paddingX: spacing.base,
      paddingY: '10px',
      borderRadius: borderRadius.md,
      transition: transitions.default,
      fontSize: typography.fontSize.body,
      fontWeight: typography.fontWeight.semibold,
      // SYN-2738 (A9): fundos/texto do DSButton por tema (--button-*).
      primaryBg: figmaColors.primary,
      primaryBgHover: figmaColors.primaryDark,
      // C12 (2026-10-05): texto do botão ghost/de texto. Aqui = a cor primária de sempre.
      ghostText: figmaColors.primary,
      secondaryBg: figmaColors.secondary,
      secondaryBgHover: figmaColors.secondaryDark,
      accentText: figmaColors.darkGray900,
    },
    card: {
      padding: spacing.xl,
      borderRadius: borderRadius.lg,
      backgroundColor: figmaColors.surfaceLight,
      boxShadow: shadows.base,
    },
    input: {
      paddingX: spacing.base,
      paddingY: '10px',
      borderRadius: borderRadius.md,
      borderColor: figmaColors.lightGray300,
      focusBorderColor: figmaColors.primary,
      fontSize: typography.fontSize.body,
    },
    chip: {
      paddingX: spacing.md,
      paddingY: spacing.xs,
      borderRadius: borderRadius.full,
      fontSize: typography.fontSize.labelSmall,
      fontWeight: typography.fontWeight.medium,
    },
    badge: {
      paddingX: spacing.sm,
      paddingY: spacing.xs,
      borderRadius: borderRadius.sm,
      fontSize: typography.fontSize.labelSmall,
      fontWeight: typography.fontWeight.bold,
    },
    sidebar: {
      backgroundColor: figmaColors.backgroundLight,
      iconSelected: figmaColors.primary,
      iconDefault: figmaColors.mediumGray600,
      iconDisabled: figmaColors.lightGray400,
      iconStatic: figmaColors.darkGray800,
      divider: figmaColors.lightGray300,
    },
  },

  // Vuetify theme configuration
  vuetifyTheme: {
    dark: false,
    colors: {
      primary: figmaColors.primary,
      secondary: figmaColors.secondary,
      accent: figmaColors.accent,
      error: figmaColors.error,
      warning: figmaColors.warning,
      info: figmaColors.info,
      success: figmaColors.success,
      background: figmaColors.backgroundLight,
      surface: figmaColors.surfaceLight,
    },
  },
};
